import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, Activity } from "lucide-react";
import { toast } from "sonner";

const ACTION_COLOR = {
    CREATE: "bg-emerald-50 text-emerald-700 border-emerald-200",
    UPDATE: "bg-amber-50 text-amber-700 border-amber-200",
    DELETE: "bg-red-50 text-red-700 border-red-200",
    LOGIN: "bg-blue-50 text-blue-700 border-blue-200",
    LOGOUT: "bg-slate-100 text-slate-700 border-slate-200",
    SETUP: "bg-primary/5 text-primary border-primary/20",
    PASSWORD_CHANGE: "bg-primary/5 text-primary border-primary/20",
};

const ENTITY_LABEL = {
    task: "Task",
    user: "User",
    client: "Client",
    auth: "Auth",
};

export default function AuditLog() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entity, setEntity] = useState("all");
    const [action, setAction] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (entity !== "all") params.entity_type = entity;
            if (action !== "all") params.action = action;
            const { data } = await api.get("/audit", { params });
            setLogs(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity, action]);

    return (
        <div className="space-y-6" data-testid="audit-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Transparency
                </div>
                <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                    Activity log
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Every login, change, and deletion is recorded — sortable by entity and action.
                </p>
            </div>

            <Card className="sdc-card p-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                        <Select value={entity} onValueChange={setEntity}>
                            <SelectTrigger data-testid="audit-entity-trigger">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All entities</SelectItem>
                                <SelectItem value="task">Tasks</SelectItem>
                                <SelectItem value="client">Clients</SelectItem>
                                <SelectItem value="user">Users</SelectItem>
                                <SelectItem value="auth">Auth</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Select value={action} onValueChange={setAction}>
                            <SelectTrigger data-testid="audit-action-trigger">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All actions</SelectItem>
                                <SelectItem value="CREATE">Create</SelectItem>
                                <SelectItem value="UPDATE">Update</SelectItem>
                                <SelectItem value="DELETE">Delete</SelectItem>
                                <SelectItem value="LOGIN">Login</SelectItem>
                                <SelectItem value="LOGOUT">Logout</SelectItem>
                                <SelectItem value="SETUP">Setup</SelectItem>
                                <SelectItem value="PASSWORD_CHANGE">Password change</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </Card>

            <Card className="sdc-card overflow-hidden">
                {loading ? (
                    <div className="grid place-items-center py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : logs.length === 0 ? (
                    <div className="py-16 text-center">
                        <Activity className="mx-auto h-10 w-10 text-muted-foreground" />
                        <p className="mt-3 text-sm text-muted-foreground">
                            No matching activity yet.
                        </p>
                    </div>
                ) : (
                    <ol className="divide-y divide-border">
                        {logs.map((l) => (
                            <li
                                key={l.id}
                                className="px-5 py-4 sdc-table-row"
                                data-testid={`audit-row-${l.id}`}
                            >
                                <div className="flex flex-wrap items-center gap-2">
                                    <span
                                        className={`sdc-pill border ${
                                            ACTION_COLOR[l.action] ||
                                            "bg-muted text-muted-foreground border-border"
                                        }`}
                                    >
                                        {l.action}
                                    </span>
                                    <span className="sdc-pill border-border bg-secondary/60 text-foreground">
                                        {ENTITY_LABEL[l.entity_type] || l.entity_type}
                                    </span>
                                    <span className="text-sm text-foreground">
                                        <span className="font-semibold">
                                            {l.user_name || l.user_email}
                                        </span>{" "}
                                        {l.entity_name && (
                                            <>
                                                on{" "}
                                                <span className="font-medium">
                                                    {l.entity_name}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                    <span className="ml-auto text-xs text-muted-foreground">
                                        {new Date(l.created_at).toLocaleString("en-IN")}
                                    </span>
                                </div>
                                {l.changes && Object.keys(l.changes).length > 0 && (
                                    <ul className="mt-2 grid grid-cols-1 gap-1.5 md:grid-cols-2">
                                        {Object.entries(l.changes).map(([k, v]) => (
                                            <li
                                                key={k}
                                                className="rounded bg-secondary/50 px-2.5 py-1 text-xs"
                                            >
                                                <span className="font-semibold">{k}</span>:{" "}
                                                {v && typeof v === "object" && "from" in v ? (
                                                    <>
                                                        <span className="text-muted-foreground">
                                                            {String(v.from ?? "—")}
                                                        </span>{" "}
                                                        →{" "}
                                                        <span className="text-foreground">
                                                            {String(v.to ?? "—")}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-foreground">
                                                        {String(v)}
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ol>
                )}
            </Card>
        </div>
    );
}
